<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Dokumentation</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('documentation')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        v-if="canCreateDocumentation"
                        aria-label="Dokumentation anlegen"
                        class="btn-stage-icon-plain"
                        :to="documentationNewPath"
                        title="Dokumentation anlegen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getDocumentations"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

        <div class="relative flex-1 min-h-0 overflow-hidden">
        <div class="h-full overflow-auto sm:overflow-hidden">
        <template v-if="documentationList.length > 0">
                <div class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border">
                    <div class="stage-listview-split-header">
                    <table class="stage-listview-split-table table-listview documentation-list-split-table">
                        <colgroup>
                            <col class="documentation-list-col-title">
                            <col v-if="showInternalPermissionColumns" class="w-[11rem]">
                            <col v-if="showInternalPermissionColumns" class="w-[5rem]">
                            <col class="w-[8rem]">
                            <col class="w-[14rem]">
                            <col class="w-[12rem]">
                            <col v-if="showActionsColumn" class="w-24">
                        </colgroup>
                        <thead>
                        <tr>
                            <th class="table-listview-l-th documentation-list-col-title">
                                Titel
                            </th>
                            <th v-if="showInternalPermissionColumns" class="table-listview-l-th">
                                L
                            </th>
                            <th v-if="showInternalPermissionColumns" class="table-listview-l-th">
                                S
                            </th>
                            <th class="table-listview-l-th">
                                Dokumente
                            </th>
                            <th class="table-listview-l-th">
                                Erstellt von
                            </th>
                            <th class="table-listview-l-th">
                                Letztes Update
                            </th>
                            <th v-if="showActionsColumn" class="table-listview-l-th table-listview-l-th-right w-24">
                                Aktionen
                            </th>
                        </tr>
                        </thead>
                    </table>
                </div>
                <div class="stage-listview-split-body">
                    <table class="stage-listview-split-table table-listview table-listview--zebra documentation-list-split-table">
                        <colgroup>
                            <col class="documentation-list-col-title">
                            <col v-if="showInternalPermissionColumns" class="w-[11rem]">
                            <col v-if="showInternalPermissionColumns" class="w-[5rem]">
                            <col class="w-[8rem]">
                            <col class="w-[14rem]">
                            <col class="w-[12rem]">
                            <col v-if="showActionsColumn" class="w-24">
                        </colgroup>
                        <tbody class="bg-white">
                        <template v-for="documentation in documentationList" :key="'documentation-row-' + documentation.id">
                            <tr
                                class="cursor-pointer"
                                @click="$router.push(documentationManagePath(documentation.id))"
                            >
                                <td class="table-listview-l-td documentation-list-col-title">
                                    <div class="flex items-start min-w-0">
                                        <div class="h-10 w-10 mr-3 text-gray-400 flex-shrink-0 flex items-center justify-center">
                                            <svg-vue class="h-6 w-6" icon="font-awesome.regular.book-open-cover"></svg-vue>
                                        </div>
                                        <div class="min-w-0">
                                            <div class="text-sm font-medium text-gray-900 truncate">
                                                {{ documentation.title }}
                                            </div>
                                            <div class="text-sm text-gray-500 truncate max-w-xl">
                                                {{ getBodyPreview(documentation.body_excerpt) }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td v-if="showInternalPermissionColumns" class="table-listview-l-td whitespace-no-wrap">
                                    <div class="flex items-center">
                                        <template v-for="(permissionUser, index) in getVisibleReadPermissionUsers(documentation)" :key="'documentation-read-permission-' + documentation.id + '-' + permissionUser.id">
                                            <img
                                               
                                                :src="permissionUser.avatar !== 'gravatar' ? permissionUser.avatar : permissionUser.gravatar"
                                                :title="permissionUser.name"
                                                :class="index === 0 ? '' : '-ml-2'"
                                                class="inline-block h-6 w-6 min-h-[1.5rem] min-w-[1.5rem] flex-shrink-0 rounded-full border border-white object-cover"
                                            >
                                        </template>
                                        <span
                                            v-if="getReadPermissionOverflowCount(documentation) > 0"
                                            class="inline-flex items-center justify-center h-6 w-6 rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-500 ml-1"
                                            title="Weitere Leseberechtigungen"
                                        >
                                            +
                                        </span>
                                        <span v-if="documentation.read_permissions_count < 1" class="text-sm text-gray-500">
                                            -
                                        </span>
                                    </div>
                                </td>
                                <td v-if="showInternalPermissionColumns" class="table-listview-l-td whitespace-no-wrap">
                                    <div class="text-sm text-gray-500">
                                        <svg
                                            v-if="documentation.is_readonly"
                                            class="h-4 w-4 text-gray-500"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7 10V8C7 5.24 9.24 3 12 3C14.76 3 17 5.24 17 8V10"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                            <rect
                                                x="5"
                                                y="10"
                                                width="14"
                                                height="11"
                                                rx="2"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                            />
                                            <path
                                                d="M12 14V17"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                        <span v-else>-</span>
                                    </div>
                                </td>
                                <td class="table-listview-l-td whitespace-no-wrap">
                                    <div class="text-sm text-gray-500">
                                        {{ documentation.documents_count }}
                                    </div>
                                </td>
                                <td class="table-listview-l-td whitespace-no-wrap">
                                    <div class="flex items-center min-w-0">
                                        <img
                                            :src="getUserAvatar(documentation.user)"
                                            :alt="documentation.user ? documentation.user.name : 'Avatar'"
                                            class="h-6 w-6 min-h-[1.5rem] min-w-[1.5rem] flex-shrink-0 rounded-full object-cover mr-3"
                                        >
                                        <span class="text-sm text-gray-900 truncate">
                                            {{ documentation.user ? documentation.user.name : '-' }}
                                        </span>
                                    </div>
                                </td>
                                <td class="table-listview-l-td whitespace-no-wrap">
                                    <div class="text-sm text-gray-500">
                                        {{ $momentFormatDateTimeAgo(documentation.updated_at) }}
                                    </div>
                                </td>
                                <td v-if="showActionsColumn" class="table-listview-l-td whitespace-no-wrap text-right">
                                    <button
                                        v-if="documentation.can_delete"
                                        class="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                        type="button"
                                        title="Dokumentation löschen"
                                        aria-label="Dokumentation löschen"
                                        @click.stop="openDeleteDocumentationModal(documentation)"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                    </button>
                                    <span v-else class="text-sm text-gray-400">-</span>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <template v-else-if="!loading">
            <empty-state />
        </template>
        </div>
                    <aside
                        :aria-hidden="!filterSidebarOpen"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-3">
                                <button
                                    class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                    type="button"
                                    @click="changeSort"
                                >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="getDocumentations"
                                    >
                                        <option value="title">Titel</option>
                                        <option value="user_id">Erstellt von</option>
                                        <option value="updated_at">Letztes Update</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        @change="getDocumentations"
                                    >
                                </div>
                            </div>
                            <template v-if="showExtendedFilters">
                                <div class="stage-filter-label">Freigaben</div>
                                <div class="mb-3">
                                    <input-select
                                        id="documentation-read-permissions"
                                        v-model="filters.read_permissions"
                                        class="stage-filter-select"
                                        :options="readPermissionFilterOptions"
                                        option-label="name"
                                        @change="onFilterChange"
                                    />
                                </div>

                                <div class="stage-filter-label">Kundenfreigaben</div>
                                <div class="mb-3">
                                    <input-select
                                        id="documentation-customer-permissions"
                                        v-model="filters.customer_permissions"
                                        class="stage-filter-select"
                                        :options="customerPermissionFilterOptions"
                                        option-label="name"
                                        @change="onFilterChange"
                                    />
                                </div>

                                <div class="stage-filter-label">Schreibgeschützt</div>
                                <div class="mb-3">
                                    <input-select
                                        id="documentation-readonly"
                                        v-model="filters.readonly"
                                        class="stage-filter-select"
                                        :options="readonlyFilterOptions"
                                        option-label="name"
                                        @change="onFilterChange"
                                    />
                                </div>
                            </template>

                            <div class="stage-filter-label">Einträge pro Seite</div>
                            <div class="mb-3">
                                <input-select
                                    id="documentation-per-page"
                                    v-model="perPage"
                                    class="stage-filter-select"
                                    :options="perPageOptions"
                                    option-label="name"
                                    @change="onPerPageChange"
                                />
                            </div>
                            <div v-if="hasActiveFilters" class="col-span-3">
                                <button
                                    id="documentation-filter-reset-btn"
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <list-footer-pagination
                    :start="(pagination.perPage * pagination.currentPage) - pagination.perPage + 1"
                    :end="pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </div>

    <confirm-dialog :open="deleteDocumentationModal" title="Dokumentation löschen" text="Soll diese Dokumentation wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" headline-id="documentation-list-delete-modal-headline" @close="closeDeleteDocumentationModal" @confirm="deleteDocumentation" />
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveAccessState} from "@/utils/access-state";
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: "documentation-list",
    metaInfo() {
        return {
            title: "Dokumentation"
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const optionValue = (key, allowedValues) => {
            const value = queryString(routeQuery, key, 'all');
            return allowedValues.includes(value) ? value : 'all';
        };
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 10, [10, 25, 50, 100]);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        if (!['title', 'user_id', 'updated_at'].includes(initialSortColumn)) {
            initialSortColumn = 'updated_at';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'desc';
        }

        return {
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            filters: {
                search: queryString(routeQuery, 'list_search', null),
                read_permissions: optionValue('list_read_permissions', ['all', 'with', 'without']),
                customer_permissions: optionValue('list_customer_permissions', ['all', 'with', 'without']),
                readonly: optionValue('list_readonly', ['all', 'yes', 'no']),
            },
            sort: {
                order: initialSortOrder,
                column: initialSortColumn,
            },
            perPage: initialPerPage !== null ? initialPerPage : 10,
            loading: true,
            pagination: {
                currentPage: 1,
                perPage: 10,
                total: 0,
                totalPages: 1,
            },
            filterSidebarOpen: false,
            documentationList: [],
            deleteDocumentationModal: false,
            documentationPendingDelete: null,
        }
    },
    mounted() {
        this.getDocumentations();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        accessState() {
            return resolveAccessState(this.$store);
        },
        isCustomerDocumentationRoute() {
            return String(this.$route.path || '').startsWith('/documentations');
        },
        documentationNewPath() {
            return this.isCustomerDocumentationRoute ? '/documentations/new' : '/workspace/documentation/new';
        },
        canCreateDocumentation() {
            return this.accessState.isAdmin || this.accessState.isEmployeeLike;
        },
        showActionsColumn() {
            return this.accessState.isInternal;
        },
        showInternalPermissionColumns() {
            return this.accessState.isInternal;
        },
        showExtendedFilters() {
            return this.accessState.isInternal;
        },
        readPermissionFilterOptions() {
            return [
                {id: 'all', name: 'Alle'},
                {id: 'with', name: 'Mit Freigaben'},
                {id: 'without', name: 'Ohne Freigaben'},
            ];
        },
        customerPermissionFilterOptions() {
            return [
                {id: 'all', name: 'Alle'},
                {id: 'with', name: 'Mit Kundenfreigaben'},
                {id: 'without', name: 'Ohne Kundenfreigaben'},
            ];
        },
        readonlyFilterOptions() {
            return [
                {id: 'all', name: 'Alle'},
                {id: 'yes', name: 'Ja'},
                {id: 'no', name: 'Nein'},
            ];
        },
        perPageOptions() {
            const defaults = [10, 25, 50, 100];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || (this.showExtendedFilters && this.filters.read_permissions !== 'all')
                || (this.showExtendedFilters && this.filters.customer_permissions !== 'all')
                || (this.showExtendedFilters && this.filters.readonly !== 'all')
                || this.sort.order !== 'desc'
                || this.sort.column !== 'updated_at'
                || Number(this.perPage || 0) !== 10;
        },
    },
    methods: {
        documentationManagePath(id) {
            return this.isCustomerDocumentationRoute
                ? routeWithReturnTo('/documentations/' + id, this.$route, '/documentations/list')
                : routeWithReturnTo('/workspace/documentation/' + id + '/manage', this.$route, '/workspace/documentation');
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
        getVisibleReadPermissionUsers(documentation) {
            const users = Array.isArray(documentation?.read_permission_users) ? documentation.read_permission_users : [];
            return users.slice(0, 5);
        },
        getReadPermissionOverflowCount(documentation) {
            const users = Array.isArray(documentation?.read_permission_users) ? documentation.read_permission_users : [];
            return users.length > 5 ? users.length - 5 : 0;
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getDocumentations();
            }
        },
        changeSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.getDocumentations();
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        onPerPageChange() {
            this.page = 1;
            this.getDocumentations();
        },
        onFilterChange() {
            this.page = 1;
            this.getDocumentations();
        },
        resetFilters() {
            this.filters.search = null;
            this.filters.read_permissions = 'all';
            this.filters.customer_permissions = 'all';
            this.filters.readonly = 'all';
            this.sort.order = 'desc';
            this.sort.column = 'updated_at';
            this.perPage = 10;
            this.page = 1;
            this.getDocumentations();
        },
        syncDocumentationListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_read_permissions',
                'list_customer_permissions',
                'list_readonly',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 10), 10);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'updated_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            if (this.showExtendedFilters) {
                setQueryValue(nextQuery, 'list_read_permissions', this.filters.read_permissions, 'all');
                setQueryValue(nextQuery, 'list_customer_permissions', this.filters.customer_permissions, 'all');
                setQueryValue(nextQuery, 'list_readonly', this.filters.readonly, 'all');
            }

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        openDeleteDocumentationModal(documentation) {
            if (!documentation || !documentation.can_delete) {
                return;
            }
            this.documentationPendingDelete = documentation;
            this.deleteDocumentationModal = true;
        },
        closeDeleteDocumentationModal() {
            this.deleteDocumentationModal = false;
            this.documentationPendingDelete = null;
        },
        getDocumentations() {
            const self = this;
            self.loading = true;
            self.syncDocumentationListRouteState();
            axios.get('api/workspace/documentations', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    read_permissions: self.filters.read_permissions,
                    customer_permissions: self.filters.customer_permissions,
                    readonly: self.filters.readonly,
                }
            }).then(function (response) {
                self.documentationList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getDocumentations();
                } else {
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
        getBodyPreview(value) {
            if (!value) {
                return '-';
            }

            const stripped = String(value)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!stripped) {
                return '-';
            }

            const decoder = document.createElement('textarea');
            decoder.innerHTML = stripped;

            return (decoder.value || decoder.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim() || '-';
        },
        deleteDocumentation() {
            const self = this;
            if (!self.documentationPendingDelete || !self.documentationPendingDelete.id || !self.documentationPendingDelete.can_delete) {
                self.closeDeleteDocumentationModal();
                return;
            }

            self.loading = true;
            axios.delete('api/workspace/documentations/' + self.documentationPendingDelete.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.closeDeleteDocumentationModal();
                self.getDocumentations();
            }).catch(function () {
                self.loading = false;
                self.deleteDocumentationModal = false;
            });
        },
    }
}
</script>

<style lang="scss">
.documentation-list-split-table {
    table-layout: auto;
}

.documentation-list-col-title {
    min-width: 300px;
    width: 36%;
}
</style>
